import { addDays, isoDay, monthName, shortDayLabel } from 'helper/timeLabels';
import { isoWeek, mondayOf, startOfDay } from 'pages/planning/planningItems';

/**
 * The periods of the statistics, as in the prototype: weeks from Monday to
 * Sunday, numbered as ISO does (S43). A month or a quarter counts whole
 * weeks, those whose Thursday falls in it (as ISO puts a week in its year):
 * its totals are then the sum of the bars of its chart. A period is
 * [start, end), its weeks are their Mondays.
 */

const DAY = 86400000;
const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);
export const daysBetween = (from, to) => Math.round((to - from) / DAY);

const weeksBetween = (from, to) => {
  const weeks = [];
  let monday = mondayOf(from);
  if (addDays(monday, 3) < from) monday = addDays(monday, 7);
  for (; addDays(monday, 3) < to; monday = addDays(monday, 7))
    weeks.push(monday);
  return weeks;
};

export const makePeriod = (kind, anchor) => {
  if (kind === 'week') {
    const start = mondayOf(anchor);
    return { kind, start, end: addDays(start, 7), weeks: [start] };
  }
  const year = anchor.getFullYear();
  const first =
    kind === 'month'
      ? anchor.getMonth()
      : Math.floor(anchor.getMonth() / 3) * 3;
  const span = kind === 'month' ? 1 : 3;
  const weeks = weeksBetween(
    new Date(year, first, 1),
    new Date(year, first + span, 1),
  );
  return {
    kind,
    year,
    first,
    start: weeks[0],
    end: addDays(weeks[weeks.length - 1], 7),
    weeks,
  };
};

// A period picked freely, from its first to its last day included: its
// figures add up day by day.
export const makeRange = (from, to) => {
  const start = startOfDay(from);
  const end = addDays(startOfDay(to), 1);
  return {
    kind: 'custom',
    start,
    end,
    days: daysBetween(start, end),
    weeks: weeksBetween(start, end),
  };
};

export const daysOf = (period) =>
  Array.from({ length: daysBetween(period.start, period.end) }, (_, index) =>
    addDays(period.start, index),
  );

export const shiftPeriod = (period, delta) => {
  if (period.kind === 'custom')
    return makeRange(
      addDays(period.start, period.days * delta),
      addDays(period.end, period.days * delta - 1),
    );
  if (period.kind === 'week')
    return makePeriod('week', addDays(period.start, 7 * delta));
  return makePeriod(
    period.kind,
    new Date(
      period.year,
      period.first + delta * (period.kind === 'month' ? 1 : 3),
      1,
    ),
  );
};

// The bars of the chart: the 8 last weeks for a week, else its own weeks.
export const chartWeeks = (period) =>
  period.kind === 'week'
    ? Array.from({ length: 8 }, (_, index) =>
        addDays(period.start, 7 * (index - 7)),
      )
    : period.weeks;

// A day that names the period without doubt (a week's Monday may belong to
// the month before).
export const anchorOf = (period) =>
  period.kind === 'week'
    ? period.start
    : new Date(period.year, period.first, 15);

// « 19 – 23 octobre », or « 28 septembre – 2 octobre »: Monday to Friday.
const workWeek = (monday) => {
  const friday = addDays(monday, 4);
  return monday.getMonth() === friday.getMonth()
    ? `${monday.getDate()} – ${friday.getDate()} ${monthName(friday.getMonth())}`
    : `${monday.getDate()} ${monthName(monday.getMonth())} – ${friday.getDate()} ${monthName(friday.getMonth())}`;
};

export const shortDay = (date) => shortDayLabel(isoDay(date));

/**
 * The names of a period: `short` in a sentence (« S43 », « octobre »,
 * « T4 »), `label` in the header (« S43 · 19 – 23 octobre »).
 */
export const periodNames = (period, text) => {
  if (period.kind === 'week') {
    const n = isoWeek(period.start);
    return {
      short: text('weekShort', { n }),
      label: text('weekLabel', { n, range: workWeek(period.start) }),
    };
  }
  if (period.kind === 'month')
    return {
      short: monthName(period.first),
      label: text('monthLabel', {
        month: capitalize(monthName(period.first)),
        year: period.year,
      }),
    };
  if (period.kind === 'quarter') {
    const n = period.first / 3 + 1;
    return {
      short: text('quarterShort', { n }),
      label: text('quarterLabel', {
        n,
        year: period.year,
        from: monthName(period.first),
        to: monthName(period.first + 2),
      }),
    };
  }
  const last = addDays(period.end, -1);
  const thisYear = new Date().getFullYear();
  const year =
    period.start.getFullYear() === last.getFullYear() &&
    last.getFullYear() === thisYear
      ? ''
      : ` ${last.getFullYear()}`;
  const label =
    period.days === 1
      ? `${shortDay(period.start)}${year}`
      : `${shortDay(period.start)} – ${shortDay(last)}${year}`;
  return { short: label, label };
};

// « d’octobre », « de septembre ».
export const ofMonth = (month, text) =>
  text(/^[aeiouyéèh]/i.test(monthName(month)) ? 'ofMonthVowel' : 'ofMonth', {
    month: monthName(month),
  });

export { isoWeek, mondayOf, startOfDay };
